// frontend/src/pages/Profile.jsx
// ✅ UPDATED: Dynamic Pi pricing from OKX + $0.17/credit + Fixed sharedToHomeCount
// ✅ FIXED: Use /api/generations/stats endpoint to avoid query parameter issues
// ✅ UPDATED: isCreator gating for Royalties + Styles buttons

import React from "react";
import { Link } from "react-router-dom";
import Screen from "../uizard/ui/Screen";
import { signInWithPi, isPiAuthenticated } from "../lib/identity";
import { usePiPrice } from "../contexts/PriceContext";
import {
  apiGet,
  apiPost,
  normalizeListResponse,
  getImageUrl,
  toast,
  fromNow,
  shorten,
  fmtPi
} from "../lib/api";

const STYLE_GUIDES_PATH = "/pricing";
const CREATOR_PROGRAM_PATH = "/creator-directory";

const brand = {
  purple: "#6D5EF9",
  purpleDark: "#5B4EE8",
  purpleTint: "#F3F0FF",
  purpleBorder: "#E7E2FF",
  neutralText: "#111827",
  neutralBorder: "#E5E7EB",
  shadow: "0 10px 24px rgba(17,24,39,.08)",
};

const USD_PER_CREDIT = 0.17;

function hasPi() {
  return (
    typeof window !== "undefined" &&
    window.Pi &&
    typeof window.Pi.createPayment === "function"
  );
}

function BuyCredits({ onAfterPurchase, currentCredits }) {
  const [qty, setQty] = React.useState(12);
  const { piPrice, piToUsd, usdToPi, loading: priceLoading } = usePiPrice();

  const clamped = Math.max(1, Math.min(10000, Number.isFinite(+qty) ? +qty : 1));
  const usdAmount = clamped * USD_PER_CREDIT;
  const piAmount = usdToPi(usdAmount);

  async function handlePurchase() {
    try {
      if (!hasPi()) {
  toast("Please open in Pi Browser to make a purchase.");
  return;
}

if (!isPiAuthenticated()) {
  toast("Please sign in with Pi first — try refreshing the page.");
  return;
}

      console.log("[BuyCredits] Starting payment:", { credits: clamped, pi: piAmount });

      await new Promise((resolve, reject) => {
        window.Pi.createPayment(
          {
            amount: piAmount,
            memo: `ArtiBytes ${clamped} credits`,
            metadata: { credits: clamped },
          },
          {
            onReadyForServerApproval: async (paymentId) => {
              console.log("[BuyCredits] Phase 1 - approving:", paymentId);
              try {
                await apiPost("/api/payments/approve", { paymentId });
                console.log("[BuyCredits] Phase 1 - approved ✅");
              } catch (e) {
                // Server blocked this because an older approved-but-unsigned
                // payment is still pending. Cancel it and retry once — the
                // current purchase then proceeds seamlessly.
                if (e?.status === 409 && e?.data?.pendingPaymentId) {
                  try {
                    console.log("[BuyCredits] Pending payment found — clearing:", e.data.pendingPaymentId);
                    await apiPost("/api/payments/cancel-incomplete", {
                      paymentId: e.data.pendingPaymentId,
                    });
                    await apiPost("/api/payments/approve", { paymentId });
                    console.log("[BuyCredits] Phase 1 - approved after clearing pending ✅");
                    return;
                  } catch (retryErr) {
                    console.error("[BuyCredits] Retry after clearing pending failed:", retryErr);
                    reject(retryErr);
                    return;
                  }
                }
                console.error("[BuyCredits] Approval failed:", e);
                reject(e);
              }
            },

            onReadyForServerCompletion: async (paymentId, txId) => {
              console.log("[BuyCredits] Phase 3 - completing:", { paymentId, txId });
              try {
                const result = await apiPost("/api/payments/complete", {
                  paymentId,
                  txId,
                });
                console.log("[BuyCredits] Phase 3 - completed ✅", result);
                toast(`✅ +${clamped} credits added!`);
                const fresh = await apiGet("/api/credits");
                onAfterPurchase?.(fresh.credits ?? currentCredits + clamped);
                resolve(result);
              } catch (e) {
                console.error("[BuyCredits] Completion failed:", e);
                reject(e);
              }
            },

            onCancel: (paymentId) => {
              console.log("[BuyCredits] Payment cancelled:", paymentId);
              toast("Payment cancelled.");
              resolve(null);
            },

            onError: (error, payment) => {
              console.error("[BuyCredits] Payment error:", error, payment);
              reject(error);
            },
          }
        );
      });

    } catch (e) {
      console.error("[BuyCredits] Purchase error:", e);
      toast(e?.message || "Could not complete purchase");
    }
  }

  return (
    <div className="buy-inline">
      <div className="buy-inline-left">
        <label className="field">
          <span className="field__label">Select amount</span>
          <input
            className="input"
            type="number"
            min={1}
            step={1}
            value={qty}
            onChange={(e) => setQty(e.target.value)}
          />
        </label>

        <div className="buy-pills">
          {[12, 24, 60, 120].map((n) => (
            <button key={n} className="pill" onClick={() => setQty(n)}>
              +{n}
            </button>
          ))}
        </div>
      </div>

      <div className="buy-inline-right">
        <div className="buy-summary-compact">
          <div className="row">
            <span>Selected Credits</span>
            <strong>{clamped}</strong>
          </div>
          <div className="row">
            <span>Pi Amount</span>
            <strong>
              {priceLoading ? "..." : `${piAmount.toFixed(6)} π`}
            </strong>
          </div>
          <div className="row price-rate">
            <span>Live Rate</span>
            <span className="price-badge">
              {priceLoading ? "..." : `1 π = $${piPrice?.toFixed(4)}`}
            </span>
          </div>
        </div>

        <button
          className="btn btn-primary buy-btn"
          onClick={handlePurchase}
          disabled={priceLoading}
        >
          Buy {clamped} credits · {priceLoading ? "..." : `${piAmount.toFixed(4)} π`}
        </button>

        <div className="hint" style={{ textAlign: "center", fontWeight: "600", color: "#6D5EF9", fontSize: "11px" }}>
          Live Rate updates in real-time
        </div>
      </div>
    </div>
  );
}

/* ------------------------------- Component -------------------------------- */
export default function Profile() {
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState("");
  const [credits, setCredits] = React.useState(0);
  const [user, setUser] = React.useState({ uid: "", username: "" });
  // ✅ isCreator gates Royalties + Styles buttons
  const [isCreator, setIsCreator] = React.useState(false);
  const [items, setItems] = React.useState([]);
  const [totalImagesCount, setTotalImagesCount] = React.useState(0);
  const [sharedToHomeCount, setSharedToHomeCount] = React.useState(0);
  const [stylesUsedCount, setStylesUsedCount] = React.useState(0);

  const [receiptsLoading, setReceiptsLoading] = React.useState(true);
  const [receiptsErr, setReceiptsErr] = React.useState("");
  const [receipts, setReceipts] = React.useState([]);

  const [payOpen, setPayOpen] = React.useState(() => {
    try { return localStorage.getItem("pf_pay_open") !== "0"; } catch { return true; }
  });

  const [statsOpen, setStatsOpen] = React.useState(() => {
    try { return localStorage.getItem("pf_stats_open") === "1"; } catch { return false; }
  });

  function togglePay() {
    setPayOpen((v) => {
      const next = !v;
      try { localStorage.setItem("pf_pay_open", next ? "1" : "0"); } catch {}
      return next;
    });
  }

  function toggleStats() {
    setStatsOpen((v) => {
      const next = !v;
      try { localStorage.setItem("pf_stats_open", next ? "1" : "0"); } catch {}
      return next;
    });
  }

  const refreshAllData = React.useCallback(async () => {
    try {
      const [creditsData, receiptsData] = await Promise.all([
        apiGet("/api/credits"),
        apiGet("/api/credits/receipts"),
      ]);

      setCredits(creditsData.credits ?? 0);
      if (Array.isArray(receiptsData)) {
        setReceipts(normalizeReceipts(receiptsData));
      }
    } catch (e) {
      console.warn("[Profile] Refresh failed:", e);
    }
  }, []);

  React.useEffect(() => {
    if (!hasPi()) return;

    const checkIncomplete = async () => {
      try {
        await signInWithPi();
        refreshAllData();
      } catch (e) {
        console.warn("[Profile] Sign in check failed:", e);
      }
    };

    checkIncomplete();
  }, [refreshAllData]);

  React.useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);

        const cj = await apiGet("/api/credits");
        if (alive) {
          setCredits(cj.credits ?? 0);
          setUser({ uid: cj.uid || "", username: cj.username || "" });
          // ✅ Read isCreator from /api/credits response
          setIsCreator(!!cj.isCreator);
        }

        const [gj, statsData] = await Promise.all([
          apiGet("/api/generations"),
          apiGet("/api/generations/stats")
        ]);

        const { items: generations } = normalizeListResponse(gj);

        if (alive) {
          setItems(generations);
          setTotalImagesCount(statsData.totalCount || 0);
          setSharedToHomeCount(statsData.sharedToHomeCount || 0);
          setStylesUsedCount(statsData.stylesUsedCount || 0);
        }
      } catch (e) {
        if (alive) setErr(e.message || "Failed to load profile");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    (async () => {
      try {
        setReceiptsLoading(true);
        setReceiptsErr("");
        const j = await apiGet("/api/credits/receipts");
        if (alive) setReceipts(normalizeReceipts(j));
      } catch (e) {
        if (alive) setReceiptsErr(e.message || "Failed to load payments");
      } finally {
        if (alive) setReceiptsLoading(false);
      }
    })();

    return () => { alive = false; };
  }, []);

  const total = totalImagesCount;
  const stylesUsed = stylesUsedCount;
  const sharedToHome = sharedToHomeCount;
  const recent = items.slice(0, 12);

  function copyUid() {
    if (!user.uid) return;
    navigator.clipboard.writeText(user.uid)
      .then(() => toast("User ID copied"))
      .catch(() => toast("Couldn't copy"));
  }

  function copyTx(tx) {
    if (!tx) return;
    navigator.clipboard.writeText(String(tx))
      .then(() => toast("Transaction hash copied"))
      .catch(() => toast("Couldn't copy"));
  }

  function exportCSV() {
    if (!receipts.length) return toast("Nothing to export");
    const header = ["date","credits","pi","status","txHash","id"].join(",");
    const rows = receipts.map((r, i) =>
      [
        new Date(r.createdAt).toISOString(),
        r.credits ?? "",
        r.pi ?? "",
        r.status ?? "",
        r.txHash ?? "",
        r.id ?? `rcpt-${i}`,
      ].map(v => (String(v).includes(",") ? `"${String(v).replace(/"/g,'""')}"` : String(v))).join(",")
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "artibytes-receipts.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function refreshCredits() {
    try {
      const j = await apiGet("/api/credits");
      setCredits(j.credits ?? credits);
    } catch (e) {
      console.error("[Profile] Refresh credits failed:", e);
    }
  }

  function formatStyleName(style) {
    const normalized = String(style || "").toLowerCase().trim();
    if (!normalized || normalized === "default") return "Standard";
    return normalized
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  return (
    <Screen>
      <StyleTag />

      {/* SECTION 1: User Info */}
      <section className="pf-hero">
        <div className="pf-hero__left">
          <div className="pf-avatar" aria-hidden>
            {user.username?.replace("@", "")?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="pf-title">
            <h1
              className="h1"
              style={{
                background: "linear-gradient(135deg, #1E3A8A 0%, #6D28D9 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Hi, {isCreator ? "Creator" : "there"}
            </h1>
            <div className="pf-id">
              {user.uid && (
                <button className="link" onClick={copyUid} title="Copy user ID">
                  {shorten(user.uid)}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: Primary Actions
          ✅ Royalties + Styles only visible to creators
          ✅ Everyone sees Activity */}
      <section className="pf-actions-compact">
        {isCreator && (
          <Link to={CREATOR_PROGRAM_PATH} className="btn">
            Royalties
          </Link>
        )}
        {isCreator && (
          <Link to="/manage-styles" className="btn">
            Styles
          </Link>
        )}
        <button className="btn" onClick={toggleStats}>
          Your Activity
        </button>
      </section>

      {/* SECTION 3: Stats & Payment History - Collapsible */}
      {statsOpen && (
        <>
          <section className="pf-stats">
            <Stat label="Total images" value={total} />
            <Stat label="Custom styles used" value={stylesUsed} />
            <Stat label="Shared to Home" value={sharedToHome} />
          </section>

          <section className="pf-payment-history panel">
            <div className="section-head">
              <div>
                <h3 className="section-title">Payment History</h3>
                {receipts.length > 0 && (
                  <div className="section-subtle">{receipts.length} transactions</div>
                )}
              </div>

              <div className="panel-tools">
                <button className="btn btn-xs" onClick={exportCSV}>Export</button>
                <button className="btn btn-xs" onClick={refreshCredits}>Refresh</button>
                <button className="btn btn-xs" onClick={togglePay}>
                  {payOpen ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            {payOpen && (
              <>
                {receiptsLoading ? (
                  <div className="pay-list">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="pay-item sk">
                        <div className="sk-line w40" />
                        <div className="sk-line w60" />
                      </div>
                    ))}
                  </div>
                ) : receiptsErr ? (
                  <div className="pf-empty">Couldn't load payments – {receiptsErr}</div>
                ) : !receipts.length ? (
                  <div className="pf-empty">No purchases yet. Buy credits to see receipts here.</div>
                ) : (
                  <div className="pay-list">
                    {receipts.map((r) => (
                      <div key={r.id || r.txHash} className="pay-item">
                        <div className="pay-top">
                          <div className="pay-title">
                            +{r.credits ?? "–"} credits
                            {typeof r.pi === "number" ? <span className="muted"> • {r.pi} π</span> : null}
                          </div>
                          <span className={`badge ${badgeCls(r.status)}`}>{labelStatus(r.status)}</span>
                        </div>
                        <div className="pay-sub">
                          <span className="muted">{fromNow(r.createdAt)}</span>
                          {r.txHash ? (
                            <>
                              <span className="dot">•</span>
                              <button className="link" onClick={() => copyTx(r.txHash)}>
                                {shorten(r.txHash)}
                              </button>
                            </>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>
        </>
      )}

      {/* SECTION 4: Credits */}
      <section className="pf-financial panel">
        <div className="panel-head">
          <div className="credits-balance-badge">
            <span className="credits-label">Available credits</span>
            <strong className="credits-number">{credits}</strong>
          </div>

          <div className="credits-explainer">
            <div className="credits-explainer-title">How credits are used</div>
            <ul>
              <li>Generate an image <span>→</span> <strong>1 credit</strong></li>
              <li>Edit or refine a prompt <span>→</span> <strong>1 credit</strong></li>
            </ul>
          </div>
        </div>

        <div className="financial-section">
          <BuyCredits
            currentCredits={credits}
            onAfterPurchase={(newCredits) => {
              setCredits(newCredits);
              refreshAllData();
            }}
          />
        </div>
      </section>

      {/* SECTION 5: Recent Activity */}
      <section className="pf-recent">
        <header>
          <h2 className="h2">Your latest generated images</h2>
          <Link to="/gallery" className="link">View all →</Link>
        </header>

        {loading ? (
          <div className="ribbon">
            {Array.from({ length: 10 }).map((_, i) => <div key={i} className="sk-tile" />)}
          </div>
        ) : recent.length === 0 ? (
          <div className="pf-empty">Nothing here yet – head to Create and make something!</div>
        ) : (
          <div className="ribbon">
            {recent.map((it) => {
              const src = getImageUrl(it);
              return (
                <figure key={it.id} className="tile" title={it.prompt || ""}>
                  <img src={src} alt={it.prompt || "generation"} loading="lazy" />
                  <span className="chip">{formatStyleName(it.style)}</span>
                </figure>
              );
            })}
          </div>
        )}
      </section>

      {err && <div className="pf-error">{err}</div>}
    </Screen>
  );
}

function Stat({ label, value }) {
  return (
    <div className="stat">
      <div className="stat__value">{value}</div>
      <div className="stat__label">{label}</div>
    </div>
  );
}

function normalizeReceipts(arr) {
  return arr.map((r, i) => ({
    id: r.id || r._id || r.txId || r.txHash || `rcpt-${i}`,
    credits: r.credits ?? r.amount ?? r.qty ?? null,
    pi: typeof r.pi === "number" ? r.pi : (r.pi ? Number(r.pi) : null),
    txHash: r.txHash ?? r.tx ?? null,
    createdAt: r.createdAt ?? r.time ?? r.date ?? Date.now(),
    status: (r.status || "confirmed").toLowerCase(),
  }));
}

function badgeCls(status = "") {
  const s = String(status).toLowerCase();
  if (s === "confirmed" || s === "paid" || s === "complete") return "ok";
  if (s === "pending" || s === "processing") return "warn";
  if (s === "failed" || s === "reversed" || s === "refunded") return "err";
  return "muted";
}

function labelStatus(status = "") {
  const s = String(status).toLowerCase();
  if (s === "ok" || s === "confirmed") return "Confirmed";
  if (s === "paid") return "Paid";
  if (s === "complete") return "Complete";
  if (s === "pending" || s === "processing") return "Pending";
  if (s === "failed") return "Failed";
  if (s === "reversed") return "Reversed";
  if (s === "refunded") return "Refunded";
  return s || "–";
}

function StyleTag() {
  return (
    <style>{`
.pf-hero{
  display:flex; align-items:center; justify-content:flex-start;
  background: linear-gradient(180deg, ${brand.purpleTint}, #fff);
  border:1px solid ${brand.purpleBorder};
  border-radius:16px; padding:16px 18px; margin-bottom:20px;
}
.pf-hero__left{ display:flex; gap:12px; align-items:center; }
.pf-avatar{
  width:52px; height:52px; border-radius:50%;
  display:grid; place-items:center;
  background:${brand.purple}; color:#fff; font-weight:800;
  box-shadow:${brand.shadow};
}
.pf-title h1{ margin:0 0 4px 0; font-size:32px; }
.pf-id{ color:#6B7280; display:flex; align-items:center; gap:8px; }
@media (max-width: 720px){
  .pf-title h1{ font-size:24px; }
}
.link{ color:${brand.purple}; font-weight:700; background:none; border:none; padding:0; cursor:pointer; text-decoration: none; }
.link:hover{ text-decoration: underline; }

.pf-actions-compact{
  display:flex; gap:10px; flex-wrap:wrap; margin-bottom:16px;
}
.pf-actions-compact .btn{
  flex:1; min-width:0;
}
@media (max-width: 640px){
  .pf-actions-compact .btn{
    font-size:14px; padding:0 12px;
  }
}

.credits-explainer{
  margin-top:8px; font-size:13px; color:#6B7280;
}
.credits-explainer-title{
  font-weight:800; color:#6B7280; margin-bottom:4px;
}
.credits-explainer ul{
  list-style:none; padding:0; margin:0; display:grid; gap:2px;
}
.credits-explainer li{
  display:flex; align-items:center; gap:6px;
}
.credits-explainer strong{
  color:#6B7280; font-weight:800;
}
.credits-explainer span{ opacity:0.6; }

.credits-balance-badge{
  display:inline-flex; align-items:center; gap:10px;
  height:40px; padding:6px 12px; margin-bottom:12px;
  background:#fff; border:1px solid ${brand.purpleBorder};
  color:${brand.neutralText}; border-radius:999px;
  box-shadow:0 2px 6px rgba(109,94,249,0.1); user-select:none;
}
.credits-label{ font-size:14px; font-weight:700; color:#6B7280; }
.credits-number{
  background:${brand.purple}; color:#fff; font-size:16px; font-weight:900;
  border-radius:999px; padding:4px 10px; min-width:28px; text-align:center;
}

.btn{ height:42px; padding:0 16px; border-radius:12px; border:1px solid ${brand.purpleBorder}; background:${brand.purpleTint}; color:${brand.neutralText}; font-weight:700; cursor:pointer; transition: all 0.2s; text-decoration:none; display:inline-flex; align-items:center; justify-content:center; }
.btn:hover{ background: #E9DEFF; border-color: ${brand.purple}; }
.btn-primary{ background:${brand.purple}; color:#fff; border-color:${brand.purple}; }
.btn-primary:hover{ background:${brand.purpleDark}; }
.btn:disabled{ opacity:.6; cursor:not-allowed; }
.btn.btn-xs{ height:30px; padding:0 10px; border-radius:10px; font-weight:800; }

.pf-stats{
  display:grid; grid-template-columns: repeat(3, minmax(0,1fr));
  gap:12px; margin-bottom:20px;
}
@media (max-width: 720px){
  .pf-stats{ gap:8px; }
  .stat{ padding:10px 8px; }
  .stat__value{ font-size:20px; }
  .stat__label{ font-size:11px; }
}
.stat{
  background:#fff; border:1px solid ${brand.neutralBorder}; border-radius:14px;
  padding:14px; text-align:center; box-shadow:${brand.shadow};
}
.stat__value{ font-size:28px; font-weight:800; color:${brand.neutralText}; }
.stat__label{ color:${brand.neutralText}; opacity:.7; font-size:14px; margin-top:4px; }

.pf-financial{ margin-bottom:20px; }
.financial-section{ padding:16px 0; }
.financial-section:not(:last-child){ border-bottom:1px solid ${brand.neutralBorder}; }
.section-title{ font-size:18px; font-weight:800; margin:0 0 12px 0; color:${brand.neutralText}; }
.section-head{ display:flex; align-items:center; justify-content:space-between; margin-bottom:12px; }
@media (max-width: 640px){
  .section-head{ flex-direction: column; align-items: flex-start; gap: 10px; }
  .panel-tools{ width: 100%; }
  .panel-tools .btn{ flex: 1; }
}
.section-subtle{ margin-top:4px; font-size:13px; font-weight:600; color:#6B7280; }

.panel{
  background: linear-gradient(180deg, rgba(239,238,255,0.7) 0%, rgba(255,255,255,0.9) 60%);
  border:1px solid ${brand.neutralBorder}; border-radius:16px; padding:18px;
  box-shadow:${brand.shadow};
}
.panel-head{ margin-bottom:12px; }
.panel-tools{ display:flex; gap:8px; }

.buy-inline{
  display:grid; gap:16px; grid-template-columns: 1fr;
  background:#fff; border:1px solid ${brand.neutralBorder}; border-radius:12px; padding:16px;
}
@media (min-width: 720px){
  .buy-inline{ grid-template-columns: 1.2fr 0.8fr; }
}
.buy-inline-left{ display:grid; gap:8px; }
.buy-inline-right{ display:grid; gap:8px; align-content:start; }
.field{ display:grid; gap:6px; }
.field__label{ font-weight:800; font-size:14px; color: ${brand.purple}; }
.input{
  width:100%; height:42px; border-radius:12px; border:1px solid ${brand.neutralBorder};
  padding:8px 12px; background:#fff; font-size:16px;
}
.buy-pills{ display:flex; gap:8px; flex-wrap:wrap; }
.pill{
  height:32px; padding:0 14px; border-radius:999px; border:1px solid ${brand.neutralBorder};
  background:#fff; font-weight:800; cursor:pointer; transition: all 0.2s;
}
.pill:hover{ border-color:${brand.purple}; background:${brand.purpleTint}; }
.buy-summary-compact{
  border:1px solid ${brand.neutralBorder}; border-radius:10px; padding:10px;
  display:grid; gap:6px; background:#FAFAFA;
}
.buy-summary-compact .row{
  display:flex; align-items:center; justify-content:space-between; font-size:14px;
}
.buy-summary-compact .row.price-rate{
  padding-top:6px; border-top:1px solid ${brand.neutralBorder}; margin-top:4px;
}
.price-badge{
  background:${brand.purpleTint}; color:${brand.purple}; padding:4px 8px;
  border-radius:6px; font-size:12px; font-weight:800; border:1px solid ${brand.purpleBorder};
}
.buy-btn{ width:100%; }
.hint{ color:#6B7280; font-size:12px; }
.muted{ color:#6B7280; }

.pay-list{ display:grid; gap:10px; margin-top:12px; }
.pay-item{
  border:1px solid ${brand.neutralBorder}; border-radius:12px; padding:12px; background:#fff;
}
.pay-top{ display:flex; align-items:center; justify-content:space-between; gap:8px; }
.pay-title{ font-weight:800; color:${brand.neutralText}; }
.pay-sub{ display:flex; align-items:center; gap:8px; margin-top:6px; font-size:13px; }
.dot{ color:#9CA3AF; }
.badge{
  border-radius:999px; padding:6px 10px; font-size:12px; font-weight:900;
  border:1px solid #ECEEF2; background:#fff; color:#374151;
}
.badge.ok{ background:#E8F7F0; color:#096645; border-color:#CDECDD; }
.badge.warn{ background:#FFF7E8; color:#8a5a00; border-color:#F5E6C3; }
.badge.err{ background:#FFE9E9; color:#8a1f1f; border-color:#F2C2C2; }

.pf-recent{ margin-bottom:20px; }
.pf-recent header{ display:flex; align-items:center; justify-content:space-between; margin-bottom:12px; }
.ribbon{
  display:flex; gap:12px; overflow:auto; padding-bottom:8px;
  -webkit-overflow-scrolling: touch;
}
.tile{
  position:relative; width:140px; min-width:140px; border-radius:14px; overflow:hidden;
  border:1px solid ${brand.neutralBorder}; background:#fff; box-shadow:${brand.shadow};
}
.tile img{ width:100%; aspect-ratio:1/1; object-fit:cover; display:block; }
.tile .chip{
  position:absolute; left:8px; bottom:8px; padding:4px 8px; font-size:11px; font-weight:800;
  background:${brand.purple}; color:#fff; border-radius:999px; box-shadow:${brand.shadow};
}
.sk-tile{
  width:140px; min-width:140px; height:140px; border-radius:14px;
  background: linear-gradient(90deg,#f5f5f7,#ececf2,#f5f5f7); animation: sk 1.2s infinite;
}
.sk-line{ height:12px; background:#EEE; border-radius:8px; margin-bottom:6px; }
.sk-line.w40{ width:40%; }
.sk-line.w60{ width:60%; }
.sk{ opacity:0.6; }
.pf-empty{ padding:12px; color:#6B7280; border:1px dashed ${brand.neutralBorder}; border-radius:10px; text-align:center; }
.pf-error{
  margin-top:14px; color:#B91C1C; background:#FFF7F7; border:1px solid #F2C2C2;
  border-radius:12px; padding:12px;
}
@keyframes sk { 0%{background-position:-200px 0} 100%{background-position:calc(200px + 100%) 0} }
    `}</style>
  );
}